// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartFormSmartTag.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import ConvertToDatimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';

import momentTimezone from 'moment-timezone';
import ReportPartFormConstants from 'logic/data/Izenda.ReportPartFormConstants';
import TinyMCEConstants from 'logic/data/Izenda.TinyMCEConstants';
import reportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import { forEach, isNil, get, trim, find, head, isEmpty, concat } from 'lodash/fp';
import 'share/utils/Izenda.StringUtilities';
import RepeaterNode from './Izenda.RepeaterNode';

export default class ReportPartFormSmartTag {
  configField = {};
  subTotalDom: any = null;
  type: string = null;
  setRepeaterLevel: string = null;
  name: string = null;

  get isValidSubTotalSmartTag() {
    const fieldHasConfigSubtotal = get('fieldInfo.hasSubTotal', this.configField);
    return (
      !isEmpty(this.configField) &&
      this.repeaterLevel <= this.configField.fieldInfo.repeaterLevel &&
      fieldHasConfigSubtotal
    );
  }

  get isValidSubTotalSmartTagLevel() {
    return this.repeaterLevel <= get('configField.fieldInfo.repeaterLevel', this);
  }

  constructor(smartTagConfig = {}) {
    this.subTotalDom = smartTagConfig.subTotalDom;
    this.name = smartTagConfig.name;
    this.type = smartTagConfig.type;
    this.configField = smartTagConfig.configField;
    this.repeaterLevel = smartTagConfig.repeaterLevel;
  }

  /**
   * Check if the field is set to invisible
   * @param {Object} field
   */
  isFieldInvisible(field: any): boolean {
    return $izenda(field).find('field-prop[key=\'isFieldVisible\'][value=\'false\']').length > 0;
  }

  /**
   * Get smart tag html
   * @param {String} html
   * @returns {Object} smart tag html
   */
  generateDateTimeSmartTag(htmlContent: string): string {
    const datetimeList = $izenda('<div>' + htmlContent + '</div>').find('datetime');
    datetimeList.each((index, datetime) => {
      const datetimeFormat = $izenda(datetime).text();
      const datetimeFormated = ConvertToDatimeFormat(datetimeFormat, momentTimezone());
      htmlContent = htmlContent.replace($izenda(datetime).get(0).outerHTML, datetimeFormated);
    });

    return htmlContent;
  }

  /**
   * Get smart tag html
   * @param {Array} sub total mapping
   * @param {Object} data records
   * @param {String} html content
   * @returns {Object} smart tag html
   */
  generateGrandTotalSmartTag(
    fieldElements: Array<any>,
    grandTotalMappingList: Array<any>,
    records: Array<any>,
    htmlContent: string
  ): string {
    const htmlDOM = $izenda('<div>' + htmlContent + '</div>');
    const grandTotalDOMList = $izenda(htmlDOM).find('grandtotal');
    const results = [];

    grandTotalDOMList.each((index, grandTotalDOM) => {
      const grandTotalMapping = find({ fieldNameAlias: trim(grandTotalDOM.textContent) }, grandTotalMappingList);
      const validateResult = this.validateGrandTotalSmartTag(grandTotalMapping, htmlDOM, grandTotalDOM);
      const fieldElement = find({ fieldName: get('fieldNameAlias', grandTotalMapping) }, fieldElements);
      const dataType = get('properties.dataFormattings.grandTotal.dataType', fieldElement);
      const grandTotal = get('properties.dataFormattings.grandTotal.format', fieldElement);

      const formattedData =
        grandTotal &&
        reportPartUtils.applyFormater(dataType, grandTotal.format, records[0][grandTotalMapping.columnName]);
      if (isNil(validateResult)) {
        results.push({
          dom: $izenda(grandTotalDOM).get(0).outerHTML,
          data: formattedData || records[0][grandTotalMapping.columnName]
        });
      } else {
        results.push({
          dom: $izenda(grandTotalDOM).get(0).outerHTML,
          data: validateResult
        });
      }
    });

    forEach(r => {
      htmlContent = htmlContent.replace(r.dom, r.data);
    }, results);

    return htmlContent;
  }

  /**
   * Get smart tag html
   * @param {Array} sub total mapping
   * @param {Object} data records
   * @param {String} html content
   * @returns {Object} smart tag html
   */
  validateSubTotalSmartTag(subTotalMapping, htmlContent, subTotalDOM) {
    // Find field by sub total
    const subtotalFieldName = $izenda(subTotalDOM)
      .text()
      .trim();
    let subtotalField;
    const htmlDOM = $izenda('<div>' + htmlContent + '</div>');
    $izenda(htmlDOM)
      .find('field')
      .each((index, field) => {
        if (
          $izenda(field)
            .text()
            .trim() === subtotalFieldName
        ) {
          subtotalField = field;
          return;
        }
      });

    if (isNil(subtotalField)) {
      return '&lt;subtotal&gt;' + subtotalFieldName + '&lt;/subtotal&gt;';
    }

    if (isNil(subTotalMapping)) {
      return this.isFieldInvisible(subtotalField)
        ? ''
        : String.format(ReportPartFormConstants.MESSAGES.M06, subtotalFieldName);
    }

    if (this.isFieldInvisible(subtotalField)) {
      return '';
    }

    let isWithin = checkFieldWithinRepeater(
      htmlContent,
      TinyMCEConstants.REPEATER_START,
      TinyMCEConstants.REPEATER_END
    );
    !isWithin &&
      (isWithin = checkFieldWithinRepeater(
        htmlContent,
        TinyMCEConstants.REPEATER_START_FORTABLE,
        TinyMCEConstants.REPEATER_END_FORTABLE
      ));
    if (!isWithin) {
      return String.format(ReportPartFormConstants.MESSAGES.M07, subtotalFieldName);
    }

    return null;

    function checkFieldWithinRepeater(htmlContent, startTemplate, endTemplate) {
      if (htmlContent.indexOf(startTemplate) === -1) return false;

      const outerHtmlField = $izenda(subtotalField).get(0).outerHTML;
      const latestBeginRepeater = htmlContent.lastIndexOf(startTemplate);
      const endRepeaterOfLatestRepeater = htmlContent.indexOf(endTemplate, latestBeginRepeater) + endTemplate.length;
      const latestRepeaterContent = htmlContent.substring(latestBeginRepeater, endRepeaterOfLatestRepeater);

      const tmpHtml = '<div>' + latestRepeaterContent + '</div>';
      const content = $izenda(tmpHtml).get(0).outerHTML;

      if (content.indexOf(outerHtmlField) > -1) {
        return true;
      }

      const newHtmlContent = htmlContent.replace(latestRepeaterContent, '');
      return checkFieldWithinRepeater(newHtmlContent, startTemplate, endTemplate);
    }
  }

  /**
   * Get smart tag html
   * @param {Array} sub total mapping
   * @param {Object} data records
   * @param {String} html content
   * @returns {Object} smart tag html
   */
  validateGrandTotalSmartTag(grandTotalMapping, htmlDOM, grandTotalDOM) {
    if ($izenda(grandTotalDOM).find('field').length > 0) {
      return ReportPartFormConstants.MESSAGES.M13;
    }

    const grandtotalFieldName = grandTotalDOM.textContent;
    let grandtotalField;
    $izenda(htmlDOM)
      .find('field')
      .each((index, field) => {
        if (trim($izenda(field).text()) === trim(grandtotalFieldName)) {
          grandtotalField = field;
          return;
        }
      });

    if (isNil(grandtotalField)) {
      return '&lt;grandtotal&gt;' + grandtotalFieldName + '&lt;/grandtotal&gt;';
    }

    if (isNil(grandTotalMapping)) {
      return this.isFieldInvisible(grandtotalField)
        ? ''
        : String.format(ReportPartFormConstants.MESSAGES.M09, grandtotalFieldName);
    }

    if (this.isFieldInvisible(grandtotalField)) {
      return '';
    }

    return null;
  }

  /**
   * Insert Embedded Report HTML
   * @param  {Array} List data of report part object
   * @return {String} embedded report html
   */
  updateGrandTotalHtml(fieldName: string, selectedElement: any): void {
    const grandTotalHtml = String.format(
      '<grandtotal class=\'field-wrapper\'><span class=\'icon-grandtotal\'></span>{0}</grandtotal>',
      fieldName
    );

    selectedElement.outerHTML = grandTotalHtml;
  }

  /**
   * Insert Embedded Report HTML
   * @param  {Array} List data of report part object
   * @return {String} embedded report html
   */
  updateSubTotalHtml(fieldName: string, selectedElement: any): void {
    const subTotalHtml = String.format(
      '<subtotal class=\'field-wrapper\'><span class=\'icon-subtotal\'></span>{0}</subtotal>',
      fieldName
    );

    selectedElement.outerHTML = subTotalHtml;
  }

  /**
   * Check repeater has parallel.
   * @param {Array} repeaterNode
   */
  checkParallelInRepeater(repeaterNode: RepeaterNode): boolean {
    if (isNil(repeaterNode)) return false;

    return get('childNodes.length', repeaterNode) > 1;
  }

  /**
   * Split string by place holdes
   * @param {String} originString
   * @param {Array}  placeHolderKeys
   * @return {Array}
   */
  splitByPlacesHolders(originString: string, placeHolderKeys: Array<String>): Array<String> {
    let result = [];
    forEach(item => {
      if (isEmpty(result)) {
        result = originString.split(item);
      } else {
        let chunks = [];
        forEach(subString => {
          chunks = concat(chunks, subString.split(item));
        }, result);
        result = chunks;
      }
    }, placeHolderKeys);
    return result;
  }

  /**
   *
   * @param {Array} repeaterNode
   * @param {String} newHTMLContent
   * @param {Boolean} isParallel
   */
  validateSubTotalSmartTagInRepeater(
    repeaterNode: Array<RepeaterNode>,
    htmlContent: string,
    isParallel: boolean
  ): string {
    let newHTMLContent = htmlContent;
    if (isNil(repeaterNode.placeHolderPattern)) return;
    const smartTagInContent = repeaterNode.smartTagInContent;

    if (!isEmpty(repeaterNode.placeHolderKey)) {
      const splitByPlaceHolderKey = this.splitByPlacesHolders(
        repeaterNode.placeHolderPattern,
        repeaterNode.placeHolderKey
      );

      forEach(item => {
        newHTMLContent = this.validateBySubTotalDom(item, smartTagInContent, newHTMLContent, isParallel);
      }, splitByPlaceHolderKey);

      isParallel = isParallel || this.checkParallelInRepeater(repeaterNode);

      // call recusive
      forEach(item => {
        newHTMLContent = this.validateSubTotalSmartTagInRepeater(item, newHTMLContent, isParallel);
      }, repeaterNode.childNodes);
    } else {
      newHTMLContent = this.validateBySubTotalDom(
        repeaterNode.placeHolderPattern,
        smartTagInContent,
        newHTMLContent,
        isParallel
      );
    }
    return newHTMLContent;
  }

  /**
   * Generate new html for subtotal smartag
   * @param {String} htmlContent
   * @param {Array} repeaterNodes
   */
  validationSubTotalSmartTag(htmlContent: string, repeaterNodes: Array<RepeaterNode>): string {
    const repeaterNode = head(repeaterNodes);
    if (isNil(repeaterNode)) return htmlContent;

    return this.validateSubTotalSmartTagInRepeater(repeaterNode, htmlContent, false);
  }

  /**
   * Validate smart tag by subtotal dom
   * @param {string} placeHolderPattern
   * @param {Object} smartTagInContent
   * @param {string} newHTMLContent
   */
  validateBySubTotalDom(
    placeHolderPattern: string,
    smartTagInContent: any,
    newHTMLContent: string,
    isParallel: boolean
  ): string {
    let newHTML = placeHolderPattern;
    const subtotalDoms = this.getSubTotalsByHtml(placeHolderPattern);
    subtotalDoms.each((index, subTotalDOM) => {
      const subtotal = $izenda(subTotalDOM).get(0).outerHTML;
      const smartTagConfig = find(['name', trim(subTotalDOM.textContent)], smartTagInContent);
      const validSubtotal = get('configField.fieldInfo.hasSubTotal', smartTagConfig);
      const subtotalFieldName = trim(subTotalDOM.textContent);
      const startIndex = newHTMLContent.indexOf(newHTML);
      const startLength = newHTML.length;
      let errorMessage = subtotal;

      switch (true) {
        // check smart tag has config field
        case isNil(get('configField.fieldInfo', smartTagConfig)):
          errorMessage = '&lt;subtotal&gt;' + subtotalFieldName + '&lt;/subtotal&gt;';
          break;
        // check config field is visible
        case !get('configField.fieldInfo.isVisible', smartTagConfig):
          errorMessage = '';
          break;
        // check field out side repeater
        case smartTagConfig.configField.fieldInfo && smartTagConfig.configField.fieldInfo.repeaterLevel === 0:
          errorMessage = String.format(ReportPartFormConstants.MESSAGES.M07, subtotalFieldName);
          break;
        // check field parallel
        case isParallel:
          errorMessage = ReportPartFormConstants.MESSAGES.ERROR_MESSAGE_FOR_SUBTOTAL_IN_PARALLEL;
          break;
        // check smart tag lower level with config field
        case !smartTagConfig.isValidSubTotalSmartTagLevel:
          errorMessage = ReportPartFormConstants.MESSAGES.ERROR_MESS_IN_LOW_LEVEL;
          break;
        case smartTagConfig.configField && !validSubtotal:
          // check config field has already config subtotal
          errorMessage = String.format(ReportPartFormConstants.MESSAGES.M06, subtotalFieldName);
          break;
      }

      if (!(startIndex < 0) && errorMessage !== subtotal) {
        newHTML = newHTML.replace(subtotal, errorMessage);
        newHTMLContent = String.replaceAt(newHTMLContent, startIndex, startLength, newHTML);
      }
    });

    return newHTMLContent;
  }

  /**
   *get subtotal dom by html content
   * @param {*} htmlContent
   */
  getSubTotalsByHtml(htmlContent: string): any {
    const htmlDOM = $izenda('<div>' + htmlContent + '</div>');
    return $izenda(htmlDOM).find('subtotal');
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartFormSmartTag.js